import {
  PayloadAction,
  createAction,
  createSlice,
  isAnyOf
} from '@reduxjs/toolkit'
// eslint-disable-next-line import/no-cycle
import { onLogout } from 'redux/loginInfo/loginInfoSlice'
import { NewBuyer } from 'typings/buyer'
import { BuyerInfoState, EditBuyerStatus } from 'typings/reduxStore'
import { ServerError } from 'typings/serverError'
import { editBuyer } from './async-thunks'

const initialPageLimit = 10
const initialPageNumber = 1

export const initState: BuyerInfoState = {
  dataList: [],
  uniqueID: 'accountRoleId',
  buyerCount: 0,
  isDataLoading: true,
  isBuyerResponseFail: false,
  addBuyerStatus: '',
  newBuyer: {} as NewBuyer,
  selectedItems: [],
  orderCardsState: {
    authBuyer: [],
    lostOrStolen: []
  },
  orderCardsStatus: '',
  removeBuyersStatus: '',
  editBuyerStatus: '',
  gridOption: {
    limit: initialPageLimit,
    columnID: '',
    currentPage: initialPageNumber,
    sortColumn: '',
    sortOrder: ''
  },
  error: false,
  errorMessage: '',
  isBuyerListRefresh: false,
  isLoading: false
}

export const resetBuyerState = createAction('buyerSlice/resetBuyerState')

const buyerSlice = createSlice({
  name: 'buyerSlice',
  initialState: initState,
  reducers: {
    /**
     * User has opened form to add a new authorized buyer. Clears out the relevant state.
     */
    addNewBuyerStarted: state => {
      state.addBuyerStatus = ''
      state.selectedItems = []
    },
    /**
     * User initiated the add new buyer form as a retry. Only clear the api
     * response status but retain the rest of the add buyer form state.
     */
    addNewBuyerRetryStarted: state => {
      state.addBuyerStatus = ''
    },
    updateBuyerState: (
      state: BuyerInfoState,
      action: PayloadAction<BuyerInfoState>
    ) => {
      return {
        ...state,
        ...action.payload
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(editBuyer.pending, state => {
        state.isLoading = true
        state.editBuyerStatus = ''
      })
      .addCase(editBuyer.fulfilled, (state, action) => {
        state.isLoading = false
        state.editBuyerStatus = action.payload.status as EditBuyerStatus
      })
      .addCase(editBuyer.rejected, (state, action) => {
        const error = action.payload as ServerError
        const { response } = error as ServerError
        const { message } = response || {}

        state.isLoading = false
        state.errorMessage = message
        state.editBuyerStatus = response?.businessErrors[0]
          ?.message as EditBuyerStatus
      })
      .addMatcher(isAnyOf(resetBuyerState, onLogout), state => {
        state.dataList = []
        state.selectedItems = []
        state.isDataLoading = true
        state.addBuyerStatus = ''
        state.isLoading = false
        state.gridOption.limit = initialPageLimit
        state.gridOption.currentPage = initialPageNumber
      })
  }
})

export const { updateBuyerState, addNewBuyerStarted, addNewBuyerRetryStarted } =
  buyerSlice.actions

export default buyerSlice.reducer
